export interface AdminProfile {
  id: number;
  username: string;
  email: string | null;
  name: string;
  role: 'system' | 'admin' | 'user';
  mfaVerified: boolean;
  mfaEnrolled: boolean;
  mustChangePassword: boolean;
  /** Providers the account sees (every provider for system). */
  providers: { id: number; slug: string; name: string }[];
}

export interface TotpEnrollment {
  otpauthUrl: string;
  qrDataUrl: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

// The session ended under a page (signed out elsewhere, password reset, 2FA or password change due):
// the app goes back through `me` to the right screen.
let onSessionLost: (() => void) | null = null;
export function setSessionLostHandler(handler: (() => void) | null): void {
  onSessionLost = handler;
}
const SESSION_LOST = new Set(['unauthenticated', 'mfa_required', 'password_change_required']);

export async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  // A FormData body sets its own multipart Content-Type (with the boundary).
  const json = !(init.body instanceof FormData);
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: { ...(json ? { 'Content-Type': 'application/json' } : {}), ...init.headers },
  });
  if (response.status === 204) {
    return undefined as T;
  }
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (body as { error?: { code?: string; message?: string } } | null)?.error;
    if (error?.code && SESSION_LOST.has(error.code) && !path.startsWith('/admin-api/auth/')) {
      onSessionLost?.();
    }
    throw new ApiError(
      response.status,
      error?.code ?? 'unknown',
      error?.message ?? 'Request failed',
    );
  }
  return body as T;
}

const send =
  (method: string) =>
  <T>(path: string, body?: unknown): Promise<T> =>
    call<T>(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });

export const post = send('POST');
export const patch = send('PATCH');
export const put = send('PUT');
export const del = send('DELETE');

export const api = {
  me: () => call<AdminProfile>('/admin-api/auth/me'),
  /** `mfaStep: 'verify'` when the account has 2FA on. */
  login: (identifier: string, password: string) =>
    post<{ mfaStep: 'verify' | null }>('/admin-api/auth/login', { identifier, password }),
  verifyTotp: (code: string) => post<void>('/admin-api/auth/totp/verify', { code }),
  setupTotp: (password: string) => post<TotpEnrollment>('/admin-api/auth/totp/setup', { password }),
  confirmTotp: (code: string) => post<void>('/admin-api/auth/totp/confirm', { code }),
  disableTotp: (password: string) => post<void>('/admin-api/auth/totp/disable', { password }),
  /** `currentPassword` undefined: the forced change after an account is created or reset. */
  changePassword: (currentPassword: string | undefined, newPassword: string) =>
    post<void>('/admin-api/auth/password', { currentPassword, newPassword }),
  logout: () => post<void>('/admin-api/auth/logout'),
};

/** Same rule as the hub: 8+ characters with an upper-case letter, a lower-case letter and a digit. */
export function passwordProblem(password: string): string | null {
  if (password.length < 8) return 'Mật khẩu cần ít nhất 8 ký tự.';
  if (!/[A-Z]/.test(password)) return 'Mật khẩu cần ít nhất 1 chữ hoa.';
  if (!/[a-z]/.test(password)) return 'Mật khẩu cần ít nhất 1 chữ thường.';
  if (!/\d/.test(password)) return 'Mật khẩu cần ít nhất 1 chữ số.';
  return null;
}

export function errorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'Không kết nối được máy chủ.';
  }
  const messages: Record<string, string> = {
    invalid_credentials: 'Tên đăng nhập, email hoặc mật khẩu không đúng.',
    account_locked: 'Tài khoản tạm khoá do nhập sai nhiều lần. Thử lại sau 15 phút.',
    invalid_code: 'Mã xác thực không đúng, đã dùng rồi hoặc đã hết hạn.',
    invalid_password: 'Mật khẩu hiện tại không đúng.',
    password_unchanged: 'Mật khẩu mới phải khác mật khẩu hiện tại.',
    password_change_required: 'Bạn cần đổi mật khẩu trước khi dùng tiếp.',
    mfa_already_enrolled: 'Bảo mật 2 lớp đã bật.',
    validation_failed: 'Dữ liệu nhập chưa hợp lệ.',
    unauthenticated: 'Phiên đăng nhập đã hết hạn.',
    role_forbidden: 'Vai trò của bạn không được làm thao tác này.',
    scope_forbidden: 'Việc này nằm ngoài provider hoặc service bạn được giao.',
    slug_taken: 'Slug này đã có provider hoặc service khác dùng.',
    username_taken: 'Đã có tài khoản dùng tên đăng nhập này.',
    email_taken: 'Đã có tài khoản dùng email này.',
    provider_required: 'Tài khoản user phải thuộc một provider.',
    provider_not_found: 'Không tìm thấy provider.',
    provider_has_services: 'Provider còn service. Xoá service trước.',
    member_not_eligible: 'Chỉ giao được cho user thuộc provider của service.',
    avatar_type: 'Logo phải là ảnh PNG, JPEG hoặc WebP.',
    avatar_missing: 'Chọn ảnh logo trước.',
    proxy_host_not_public: 'Host của proxy phải là địa chỉ public.',
    proxy_host_unresolvable: 'Không phân giải được host của proxy.',
    cannot_change_self: 'Không tự đổi vai trò, khoá hay reset chính mình. Nhờ tài khoản khác.',
    last_system: 'Hub phải còn ít nhất một tài khoản system đang hoạt động.',
    delivery_in_progress: 'Lần giao này vẫn đang chạy.',
    proxy_incomplete: 'Nhập lại đủ giao thức, host và port.',
    origin_not_allowed: 'Trình duyệt gửi từ origin không được phép.',
    invalid_phone: 'Số điện thoại Việt Nam không hợp lệ.',
    name_blank: 'Nhập tên của người dùng số này.',
    phone_taken: 'Số này đã có trong danh sách.',
    internal_phone_not_found: 'Không tìm thấy số này, có thể đã bị xoá.',
    lookup_account_unknown: 'Tài khoản Zalo này không thuộc service nào của provider.',
    file_missing: 'Chọn tệp CSV trước.',
    file_too_large: 'Tệp lớn hơn 1 MB.',
    too_many_rows: 'Mỗi tệp tối đa 1000 số.',
    unreadable: 'Tệp không phải CSV dạng chữ.',
  };
  return messages[error.code] ?? error.message;
}
